import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { Toaster, TooltipProvider } from '@atrvd/saas-ui'
import { AuthProvider } from '@/contexts/AuthContext'
import { OrganizationProvider } from '@/contexts/OrganizationContext'
import { queryClient } from '@/lib/queryClient'
import { SessionExpiredToastRestorer } from '@/components/auth/SessionExpiredToastRestorer'
import { configureAppToasts } from '@/lib/toast-config'
import '@atrvd/saas-ui/styles.css'
import './index.css'

/**
 * Toast policy, applied before render so it covers the very first toast raised
 * — including one from a module-scope error handler (DCP-365).
 *
 * The restore of an interrupted session-expired toast deliberately does NOT
 * live here: sonner discards a toast raised before `<Toaster />` has
 * subscribed, so it is owned by `<SessionExpiredToastRestorer />` below the
 * Toaster instead. See that component for the mechanics.
 */
configureAppToasts()

// Start MSW in development mode with mocks enabled
async function enableMocking() {
  if (import.meta.env.VITE_MOCK_API !== 'true') {
    return
  }

  try {
    const { startMockWorker } = await import('@dcp/mocks/browser')
    await startMockWorker()
  } catch (error) {
    console.error('[MSW] Failed to start mock worker:', error)
    // Continue without mocks rather than crashing
  }
}

function renderApp() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BrowserRouter>
            <AuthProvider>
              <OrganizationProvider>
                <App />
                <Toaster />
                {/* AFTER <Toaster /> on purpose — a toast raised before the
                    Toaster subscribes is discarded by sonner (DCP-365 AC.4.2). */}
                <SessionExpiredToastRestorer />
              </OrganizationProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>
  )
}

enableMocking().then(() => {
  renderApp()
})